import { useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle, Star, Users } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GetStartedDialog from "@/components/GetStartedDialog";
import CaseStudiesDialog from "@/components/CaseStudiesDialog";
import workdayImage from "@/assets/1-workday-hcm.jpg";
import workdayHrBg from "@/assets/workday-hr-bg.jpg";
import {
  DollarSign,
  Target,
  BookOpen,
  UserCheck,
  Clock,
  BarChart3,
  Shield,
  Globe,
  TrendingUp,
  MessageSquare,
  Calendar,
  FileText,
  Settings,
  Award,
  Database,
  Building,
  Cog,
  Heart,
  Zap,
  Layers,
  CheckCircle as Check,
} from "lucide-react";

interface WorkdayModuleData {
  // icon: any;
  title: string;
  // category: string;
  description: string;
  features: string[];
  technologies: string[];
  benefits: string[];
  process: string[];
  overview: string;
  implementation: {
    discovery: string[];
    design: string[];
    deployment: string[];
  };
  integrations: string[];
}

const workdayModuleData: Record<string, WorkdayModuleData> = {
  // "hcm-core": {
  //   // icon: Users,
  //   title: "Workday HCM Core",
  //   // category: "Core HR",
  //   description: "Employee profiles, organizational management, and workforce planning for comprehensive HR management.",
  //   overview: "Workday HCM Core provides the foundation for all HR operations, offering comprehensive employee data management, organizational structures, and workforce planning capabilities. It serves as the central hub for all human capital management activities.",
  //   features: ["Employee Profiles", "Organizational Management", "Workforce Planning", "Position Management", "Job Management", "Compensation Management"],
  //   technologies: ["Workday HCM", "Workday Organizational Management", "Workday Position Management", "Workday Compensation", "Workday Analytics"],
  //   benefits: ["Unified Employee Data", "Real-time Reporting", "Mobile Access", "Global Compliance", "Self-Service Capabilities", "Advanced Analytics"],
  //   process: ["Organizational Design", "Employee Data Setup", "Position Management", "Compensation Planning", "Reporting Configuration", "User Training"],
  //   implementation: {
  //     discovery: ["Current State Analysis", "Business Requirements", "Organizational Structure Review", "Data Analysis", "Integration Requirements"],
  //     design: ["System Configuration", "Business Process Design", "Security Setup", "Custom Fields", "Reporting Design"],
  //     deployment: ["Data Migration", "System Testing", "User Training", "Go-Live Support", "Post-Implementation Support"]
  //   },
  //   integrations: ["Payroll Systems", "Benefits Administration", "Time Tracking", "Learning Management", "Recruiting", "Performance Management"]
  // },
  // "payroll": {
  //   // icon: DollarSign,
  //   title: "Workday Payroll",
  //   // category: "Payroll",
  //   description: "Global payroll processing, tax management, and compensation for worldwide operations.",
  //   overview: "Workday Payroll delivers comprehensive global payroll capabilities with real-time processing, automated tax calculations, and seamless integration with HCM data. It supports complex payroll requirements across multiple countries and jurisdictions.",
  //   features: ["Global Payroll Processing", "Tax Management", "Compensation Planning", "Payroll Reporting", "Year-end Processing", "Multi-country Support"],
  //   technologies: ["Workday Payroll", "Workday Tax", "Workday Compensation", "Workday Global Payroll", "Workday Analytics"],
  //   benefits: ["Real-time Processing", "Global Compliance", "Automated Calculations", "Seamless Integration", "Employee Self-Service", "Advanced Reporting"],
  //   process: ["Payroll Design", "Tax Configuration", "Compensation Setup", "Testing", "Parallel Runs", "Go-Live"],
  //   implementation: {
  //     discovery: ["Payroll Requirements", "Tax Requirements", "Compliance Analysis", "Current System Review", "Data Mapping"],
  //     design: ["Payroll Configuration", "Tax Setup", "Compensation Design", "Reporting Setup", "Security Configuration"],
  //     deployment: ["Data Conversion", "Parallel Testing", "User Training", "Go-Live Support", "Post-Go-Live Support"]
  //   },
  //   integrations: ["HCM Core", "Time Tracking", "Benefits", "Tax Systems", "Banking", "Third-party Payroll"]
  // },
  "hcm-payroll": {
    title: "Core HCM & Payroll",
    description:
      "Unified human capital management and global payroll solution combining employee lifecycle management, workforce planning, compensation, tax, and payroll processing.",
    overview:
      "Core HCM & Payroll combines Workday HCM Core and Workday Payroll into a single integrated platform for managing employee data, organizational structures, workforce planning, payroll processing, tax compliance, and compensation management. This unified solution provides end-to-end HR and payroll operations with real-time data consistency, automation, and global scalability.",

    features: [
      // HCM Core
      "Employee Profiles",
      "Organizational Management",
      "Workforce Planning",
      "Position Management",
      "Job Management",

      // Payroll
      "Global Payroll Processing",
      "Tax Management",
      "Compensation Planning",
      "Payroll Reporting",
      "Year-end Processing",
      "Multi-country Support",
    ],

    technologies: [
      "Workday HCM",
      "Workday Payroll",
      "Workday Organizational Management",
      "Workday Position Management",
      "Workday Compensation",
      "Workday Tax",
      "Workday Global Payroll",
      "Workday Analytics",
    ],

    benefits: [
      "Unified Employee and Payroll Data",
      "Real-time Processing & Reporting",
      "Global Compliance",
      "Automated Payroll & Tax Calculations",
      "Employee Self-Service",
      "Advanced Workforce Analytics",
      "Seamless HR-Payroll Integration",
      "Scalable Global Operations",
    ],

    process: [
      "Organizational & Payroll Strategy",
      "Employee and Compensation Setup",
      "Position & Workforce Planning",
      "Payroll & Tax Configuration",
      "Reporting & Compliance Setup",
      "Testing & Parallel Runs",
      "Go-Live & Optimization",
    ],

    implementation: {
      discovery: [
        "Current State Analysis",
        "Business & Payroll Requirements",
        "Organizational Structure Review",
        "Tax & Compliance Analysis",
        "Integration Requirements",
      ],

      design: [
        "System Configuration",
        "Business Process Design",
        "Payroll & Tax Setup",
        "Compensation Design",
        "Security Configuration",
        "Reporting Framework",
      ],

      deployment: [
        "Data Migration & Conversion",
        "System & Parallel Testing",
        "User Training",
        "Go-Live Support",
        "Post-Implementation Optimization",
      ],
    },

    integrations: [
      "Benefits Administration",
      "Time Tracking",
      "Learning Management",
      "Recruiting",
      "Performance Management",
      "Tax Systems",
      "Banking",
      "Third-party Payroll",
    ],
  },
  // talent: {
  //   // icon: Target,
  //   title: "Workday Talent Management",
  //   // category: "Talent",
  //   description:
  //     "Performance reviews, goal setting, and succession planning for talent development.",
  //   overview:
  //     "Workday Talent Management provides comprehensive tools for managing employee performance, setting goals, and planning succession. It enables organizations to develop talent and align individual performance with business objectives.",
  //   features: [
  //     "Performance Management",
  //     "Goal Setting",
  //     "Succession Planning",
  //     "Career Development",
  //     "360 Reviews",
  //     "Calibration",
  //   ],
  //   technologies: [
  //     "Workday Performance Management",
  //     "Workday Goals",
  //     "Workday Career Hub",
  //     "Workday Succession Planning",
  //     "Workday Analytics",
  //   ],
  //   benefits: [
  //     "Improved Performance",
  //     "Goal Alignment",
  //     "Talent Development",
  //     "Succession Readiness",
  //     "Employee Engagement",
  //     "Data-driven Decisions",
  //   ],
  //   process: [
  //     "Performance Framework",
  //     "Goal Alignment",
  //     "Review Process Setup",
  //     "Succession Planning",
  //     "Career Path Design",
  //     "Training",
  //   ],
  //   implementation: {
  //     discovery: [
  //       "Performance Strategy",
  //       "Goal Framework",
  //       "Succession Requirements",
  //       "Career Framework",
  //       "Review Process",
  //     ],
  //     design: [
  //       "Performance Configuration",
  //       "Goal Setup",
  //       "Succession Design",
  //       "Career Planning",
  //       "Reporting Setup",
  //     ],
  //     deployment: [
  //       "Process Testing",
  //       "Manager Training",
  //       "Employee Training",
  //       "Go-Live Support",
  //       "Ongoing Support",
  //     ],
  //   },
  //   integrations: [
  //     "HCM Core",
  //     "Learning",
  //     "Recruiting",
  //     "Compensation",
  //     "Analytics",
  //     "External Assessments",
  //   ],
  // },
  "talent-recruiting": {
    title: "Talent & Recruiting",
    description:
      "Integrated talent management and recruiting solution for performance, career development, succession planning, talent acquisition, and end-to-end workforce growth.",
    overview:
      "Talent & Recruiting combines Workday Talent Management and Workday Recruiting into a unified platform that supports the full employee journey—from attracting and hiring top talent to developing, retaining, and advancing employees. It enables organizations to align recruitment, performance, career growth, and succession strategies with business goals.",

    features: [
      // Talent Management
      "Performance Management",
      "Goal Setting",
      "Succession Planning",
      "Career Development",
      "360 Reviews",
      "Calibration",

      // Recruiting
      "Applicant Tracking",
      "Interview Scheduling",
      "Offer Management",
      "Candidate Experience",
      "Recruiting Analytics",
      "Onboarding Integration",
    ],

    technologies: [
      "Workday Talent Management",
      "Workday Performance Management",
      "Workday Goals",
      "Workday Career Hub",
      "Workday Succession Planning",
      "Workday Recruiting",
      "Workday Candidate Experience",
      "Workday Interview",
      "Workday Onboarding",
      "Workday Analytics",
    ],

    benefits: [
      "End-to-End Talent Lifecycle Management",
      "Improved Hiring Efficiency",
      "Enhanced Candidate & Employee Experience",
      "Stronger Performance Alignment",
      "Succession Readiness",
      "Faster Talent Acquisition",
      "Data-driven Workforce Decisions",
      "Higher Employee Engagement & Retention",
    ],

    process: [
      "Talent & Recruiting Strategy",
      "Performance Framework Design",
      "Recruiting Workflow Configuration",
      "Goal & Career Path Alignment",
      "Interview & Offer Process Setup",
      "Succession Planning",
      "Training & Enablement",
    ],

    implementation: {
      discovery: [
        "Talent Strategy Assessment",
        "Recruiting Process Analysis",
        "Performance & Goal Framework",
        "Career & Succession Requirements",
        "Compliance & Integration Needs",
      ],

      design: [
        "Talent Configuration",
        "Recruiting Workflow Design",
        "Performance Review Setup",
        "Career Development Framework",
        "Interview & Offer Configuration",
        "Reporting & Analytics Setup",
      ],

      deployment: [
        "Process Testing",
        "Recruiter & Manager Training",
        "Employee Enablement",
        "Go-Live Support",
        "Post-Implementation Optimization",
      ],
    },

    integrations: [
      "HCM Core",
      "Learning",
      "Compensation",
      "Onboarding",
      "Job Boards",
      "Assessment Tools",
      "Background Check Systems",
      "External ATS",
      "Analytics",
    ],
  },
  learning: {
    // icon: BookOpen,
    title: "Learning & Development",
    // category: "Learning",
    description:
      "Training programs, certifications, and skill development for continuous learning.",
    overview:
      "Workday Learning provides a comprehensive learning management system that enables organizations to deliver training, track certifications, and develop employee skills. It supports both formal and informal learning experiences.",
    features: [
      "Training Programs",
      "Certification Management",
      "Skill Development",
      "Learning Analytics",
      "Mobile Learning",
      "Social Learning",
    ],
    technologies: [
      "Workday Learning",
      "Workday Skills Cloud",
      "Workday Discovery Board",
      "Workday Analytics",
      "Workday Mobile",
    ],
    benefits: [
      "Skill Development",
      "Compliance Training",
      "Certification Tracking",
      "Personalized Learning",
      "Mobile Access",
      "Analytics Insights",
    ],
    process: [
      "Learning Strategy",
      "Content Development",
      "Curriculum Design",
      "Delivery Setup",
      "Tracking Configuration",
      "User Training",
    ],
    implementation: {
      discovery: [
        "Learning Requirements",
        "Content Analysis",
        "Compliance Needs",
        "Skill Frameworks",
        "Delivery Methods",
      ],
      design: [
        "Learning Configuration",
        "Content Setup",
        "Curriculum Design",
        "Tracking Setup",
        "Reporting Design",
      ],
      deployment: [
        "Content Migration",
        "System Testing",
        "User Training",
        "Go-Live Support",
        "Content Management",
      ],
    },
    integrations: [
      "HCM Core",
      "Performance Management",
      "Recruiting",
      "External Content",
      "LTI Systems",
      "Virtual Classrooms",
    ],
  },
  // recruiting: {
  //   // icon: UserCheck,
  //   title: "Workday Recruiting",
  //   // category: "Recruiting",
  //   description:
  //     "Applicant tracking, interview scheduling, and offer management for talent acquisition.",
  //   overview:
  //     "Workday Recruiting streamlines the entire recruitment process from job posting to onboarding. It provides tools for managing candidates, scheduling interviews, and making data-driven hiring decisions.",
  //   features: [
  //     "Applicant Tracking",
  //     "Interview Scheduling",
  //     "Offer Management",
  //     "Candidate Experience",
  //     "Recruiting Analytics",
  //     "Onboarding Integration",
  //   ],
  //   technologies: [
  //     "Workday Recruiting",
  //     "Workday Candidate Experience",
  //     "Workday Interview",
  //     "Workday Onboarding",
  //     "Workday Analytics",
  //   ],
  //   benefits: [
  //     "Streamlined Process",
  //     "Better Candidate Experience",
  //     "Faster Hiring",
  //     "Data-driven Decisions",
  //     "Compliance Tracking",
  //     "Mobile Access",
  //   ],
  //   process: [
  //     "Recruiting Strategy",
  //     "Job Template Setup",
  //     "Workflow Configuration",
  //     "Interview Setup",
  //     "Offer Process",
  //     "Training",
  //   ],
  //   implementation: {
  //     discovery: [
  //       "Recruiting Process",
  //       "Job Requirements",
  //       "Workflow Analysis",
  //       "Integration Needs",
  //       "Compliance Requirements",
  //     ],
  //     design: [
  //       "Process Configuration",
  //       "Job Setup",
  //       "Workflow Design",
  //       "Interview Configuration",
  //       "Reporting Setup",
  //     ],
  //     deployment: [
  //       "Process Testing",
  //       "Recruiter Training",
  //       "Manager Training",
  //       "Go-Live Support",
  //       "Process Optimization",
  //     ],
  //   },
  //   integrations: [
  //     "HCM Core",
  //     "Job Boards",
  //     "Background Check",
  //     "Assessment Tools",
  //     "Onboarding",
  //     "External ATS",
  //   ],
  // },
  // time: {
  //   // icon: Clock,
  //   title: "Workday Time Tracking",
  //   // category: "Time",
  //   description:
  //     "Time entry, attendance, and absence management for workforce time tracking.",
  //   overview:
  //     "Workday Time Tracking provides comprehensive time and attendance management with real-time tracking, automated calculations, and integration with payroll and absence management systems.",
  //   features: [
  //     "Time Entry",
  //     "Attendance Tracking",
  //     "Absence Management",
  //     "Overtime Management",
  //     "Schedule Management",
  //     "Mobile Time Entry",
  //   ],
  //   technologies: [
  //     "Workday Time Tracking",
  //     "Workday Absence",
  //     "Workday Schedule",
  //     "Workday Mobile",
  //     "Workday Analytics",
  //   ],
  //   benefits: [
  //     "Accurate Time Tracking",
  //     "Automated Calculations",
  //     "Compliance Management",
  //     "Mobile Access",
  //     "Real-time Visibility",
  //     "Integration Benefits",
  //   ],
  //   process: [
  //     "Time Policy Setup",
  //     "Schedule Configuration",
  //     "Absence Setup",
  //     "Device Configuration",
  //     "Testing",
  //     "Training",
  //   ],
  //   implementation: {
  //     discovery: [
  //       "Time Policies",
  //       "Schedule Requirements",
  //       "Absence Policies",
  //       "Device Needs",
  //       "Integration Requirements",
  //     ],
  //     design: [
  //       "Policy Configuration",
  //       "Schedule Setup",
  //       "Absence Configuration",
  //       "Device Setup",
  //       "Reporting Design",
  //     ],
  //     deployment: [
  //       "Device Installation",
  //       "Testing",
  //       "User Training",
  //       "Go-Live Support",
  //       "Ongoing Maintenance",
  //     ],
  //   },
  //   integrations: [
  //     "HCM Core",
  //     "Payroll",
  //     "Absence Management",
  //     "Time Clocks",
  //     "Mobile Apps",
  //     "Third-party Systems",
  //   ],
  // },
  "time-absence": {
    title: "Time Tracking & Absence",
    description:
      "Integrated workforce time, attendance, scheduling, leave, and absence management solution for accurate labor tracking, compliance, and employee availability management.",
    overview:
      "Time Tracking & Absence combines Workday Time Tracking and Workday Absence Management into a unified workforce management platform that supports time entry, attendance, overtime, scheduling, leave administration, FMLA, and absence compliance. It enables organizations to manage workforce availability, labor costs, and regulatory compliance while improving employee and manager visibility.",

    features: [
      // Time Tracking
      "Time Entry",
      "Attendance Tracking",
      "Overtime Management",
      "Schedule Management",
      "Mobile Time Entry",

      // Absence
      "Absence Management",
      "Leave Tracking",
      "FMLA Administration",
      "Disability Management",
      "Case Management",
      "Return to Work",
      "Absence Analytics",
    ],

    technologies: [
      "Workday Time Tracking",
      "Workday Absence",
      "Workday Schedule",
      "Workday Mobile",
      "Workday FMLA",
      "Workday Case Management",
      "Workday Analytics",
    ],

    benefits: [
      "Accurate Workforce Time & Leave Tracking",
      "Automated Time and Absence Calculations",
      "Regulatory & Compliance Management",
      "Real-time Workforce Visibility",
      "Mobile Employee Access",
      "Integrated Payroll & HCM Support",
      "Reduced Administrative Burden",
      "Improved Labor Cost Control",
    ],

    process: [
      "Time & Absence Policy Design",
      "Schedule & Leave Configuration",
      "FMLA & Compliance Setup",
      "Case Management Design",
      "Device & Mobile Configuration",
      "Testing & Validation",
      "Training & Go-Live",
    ],

    implementation: {
      discovery: [
        "Time Policies Assessment",
        "Absence & Leave Policy Review",
        "Schedule Requirements",
        "Compliance & FMLA Analysis",
        "Integration Requirements",
      ],

      design: [
        "Time Policy Configuration",
        "Absence & Leave Setup",
        "Schedule Design",
        "Case Management Framework",
        "Workflow Configuration",
        "Reporting & Analytics Setup",
      ],

      deployment: [
        "System & Device Setup",
        "Testing & Validation",
        "User & Manager Training",
        "Go-Live Support",
        "Post-Implementation Optimization",
      ],
    },

    integrations: [
      "HCM Core",
      "Payroll",
      "Benefits",
      "Scheduling Systems",
      "Time Clocks",
      "Mobile Applications",
      "Third-party Administrators",
      "Medical Providers",
    ],
  },
  // benefits: {
  //   // icon: BarChart3,
  //   title: "Workday Benefits",
  //   // category: "Benefits",
  //   description:
  //     "Benefits administration, enrollment, and compliance for comprehensive benefits management.",
  //   overview:
  //     "Workday Benefits streamlines benefits administration with automated enrollment, plan management, and compliance tracking, providing employees with easy access to their benefits information.",
  //   features: [
  //     "Benefits Enrollment",
  //     "Plan Management",
  //     "COBRA Administration",
  //     "Flexible Spending Accounts",
  //     "Benefits Analytics",
  //     "Compliance Reporting",
  //   ],
  //   technologies: [
  //     "Workday Benefits",
  //     "Workday Open Enrollment",
  //     "Workday COBRA",
  //     "Workday FSA",
  //     "Workday Analytics",
  //   ],
  //   benefits: [
  //     "Simplified Enrollment",
  //     "Automated Administration",
  //     "Compliance Management",
  //     "Employee Self-Service",
  //     "Cost Control",
  //     "Analytics Insights",
  //   ],
  //   process: [
  //     "Benefits Strategy",
  //     "Plan Configuration",
  //     "Enrollment Setup",
  //     "Compliance Configuration",
  //     "Analytics Setup",
  //     "Training",
  //   ],
  //   implementation: {
  //     discovery: [
  //       "Benefits Strategy",
  //       "Plan Analysis",
  //       "Enrollment Process",
  //       "Compliance Requirements",
  //       "Integration Needs",
  //     ],
  //     design: [
  //       "Plan Configuration",
  //       "Enrollment Design",
  //       "Compliance Setup",
  //       "Reporting Configuration",
  //       "Security Setup",
  //     ],
  //     deployment: [
  //       "Plan Testing",
  //       "Enrollment Testing",
  //       "User Training",
  //       "Go-Live Support",
  //       "Post-Implementation Support",
  //     ],
  //   },
  //   integrations: [
  //     "HCM Core",
  //     "Payroll",
  //     "Insurance Carriers",
  //     "Third-party Administrators",
  //     "Banking",
  //     "External Benefits",
  //   ],
  // },
  // compensation: {
  //   // icon: DollarSign,
  //   title: "Workday Compensation",
  //   // category: "Compensation",
  //   description:
  //     "Salary planning, merit increases, and equity management for compensation planning.",
  //   overview:
  //     "Workday Compensation provides comprehensive compensation planning with salary benchmarking, merit planning, and equity management to attract and retain top talent.",
  //   features: [
  //     "Salary Planning",
  //     "Merit Increases",
  //     "Equity Management",
  //     "Bonus Planning",
  //     "Compensation Benchmarking",
  //     "Total Rewards Statements",
  //   ],
  //   technologies: [
  //     "Workday Compensation",
  //     "Workday Adaptive Planning",
  //     "Workday Benchmarking",
  //     "Workday Analytics",
  //   ],
  //   benefits: [
  //     "Fair Compensation",
  //     "Market Competitiveness",
  //     "Budget Control",
  //     "Transparency",
  //     "Retention Support",
  //     "Compliance",
  //   ],
  //   process: [
  //     "Compensation Strategy",
  //     "Market Analysis",
  //     "Merit Planning",
  //     "Equity Setup",
  //     "Approval Workflows",
  //     "Communication",
  //   ],
  //   implementation: {
  //     discovery: [
  //       "Compensation Philosophy",
  //       "Market Positioning",
  //       "Merit Framework",
  //       "Equity Strategy",
  //       "Budget Planning",
  //     ],
  //     design: [
  //       "Compensation Setup",
  //       "Merit Configuration",
  //       "Equity Management",
  //       "Approval Workflows",
  //       "Reporting Design",
  //     ],
  //     deployment: [
  //       "Process Testing",
  //       "Manager Training",
  //       "Employee Communication",
  //       "Go-Live Support",
  //       "Ongoing Support",
  //     ],
  //   },
  //   integrations: [
  //     "HCM Core",
  //     "Payroll",
  //     "Performance Management",
  //     "Market Data",
  //     "Stock Administration",
  //     "Banking",
  //   ],
  // },
  // absence: {
  //   // icon: Calendar,
  //   title: "Workday Absence Management",
  //   // category: "Absence",
  //   description:
  //     "Leave tracking, FMLA administration, and absence compliance for workforce management.",
  //   overview:
  //     "Workday Absence Management automates leave tracking, FMLA administration, and compliance with complex leave regulations, providing transparency for employees and managers.",
  //   features: [
  //     "Leave Tracking",
  //     "FMLA Administration",
  //     "Disability Management",
  //     "Absence Analytics",
  //     "Case Management",
  //     "Return to Work",
  //   ],
  //   technologies: [
  //     "Workday Absence",
  //     "Workday FMLA",
  //     "Workday Case Management",
  //     "Workday Analytics",
  //   ],
  //   benefits: [
  //     "Automated Tracking",
  //     "Compliance Management",
  //     "Case Management",
  //     "Employee Support",
  //     "Manager Visibility",
  //     "Cost Control",
  //   ],
  //   process: [
  //     "Absence Policy Setup",
  //     "FMLA Configuration",
  //     "Case Management",
  //     "Analytics Setup",
  //     "Training",
  //     "Go-Live",
  //   ],
  //   implementation: {
  //     discovery: [
  //       "Absence Policies",
  //       "FMLA Requirements",
  //       "Case Management Needs",
  //       "Compliance Requirements",
  //       "Integration Needs",
  //     ],
  //     design: [
  //       "Policy Configuration",
  //       "FMLA Setup",
  //       "Case Management Design",
  //       "Reporting Setup",
  //       "Workflow Configuration",
  //     ],
  //     deployment: [
  //       "Policy Testing",
  //       "Case Testing",
  //       "User Training",
  //       "Go-Live Support",
  //       "Ongoing Case Management",
  //     ],
  //   },
  //   integrations: [
  //     "HCM Core",
  //     "Time Tracking",
  //     "Payroll",
  //     "Benefits",
  //     "Third-party Administrators",
  //     "Medical Providers",
  //   ],
  // },
  "compensation-benefits": {
    title: "Compensation & Benefits",
    description:
      "Integrated total rewards solution for salary planning, compensation strategy, benefits administration, enrollment, and compliance management.",
    overview:
      "Compensation & Benefits combines Workday Compensation and Workday Benefits into a unified total rewards platform that enables organizations to manage salary structures, merit increases, bonuses, equity, benefits enrollment, plan administration, and compliance. This integrated approach helps businesses attract, retain, and support employees while maintaining cost control and regulatory alignment.",

    features: [
      // Compensation
      "Salary Planning",
      "Merit Increases",
      "Equity Management",
      "Bonus Planning",
      "Compensation Benchmarking",
      "Total Rewards Statements",

      // Benefits
      "Benefits Enrollment",
      "Plan Management",
      "COBRA Administration",
      "Flexible Spending Accounts",
      "Benefits Analytics",
      "Compliance Reporting",
    ],

    technologies: [
      "Workday Compensation",
      "Workday Adaptive Planning",
      "Workday Benchmarking",
      "Workday Benefits",
      "Workday Open Enrollment",
      "Workday COBRA",
      "Workday FSA",
      "Workday Analytics",
    ],

    benefits: [
      "Unified Total Rewards Management",
      "Competitive Compensation Strategy",
      "Simplified Benefits Administration",
      "Budget & Cost Control",
      "Employee Self-Service",
      "Compliance Management",
      "Retention & Talent Attraction",
      "Advanced Reporting & Analytics",
    ],

    process: [
      "Total Rewards Strategy",
      "Compensation & Benefits Design",
      "Market & Plan Analysis",
      "Configuration & Workflow Setup",
      "Enrollment & Approval Setup",
      "Testing & Validation",
      "Communication & Go-Live",
    ],

    implementation: {
      discovery: [
        "Compensation Philosophy",
        "Benefits Strategy",
        "Market Benchmarking",
        "Plan & Policy Analysis",
        "Compliance Requirements",
        "Integration Requirements",
      ],

      design: [
        "Compensation Framework Setup",
        "Benefits Plan Configuration",
        "Merit & Bonus Design",
        "Enrollment Workflow Design",
        "Compliance Configuration",
        "Reporting & Security Setup",
      ],

      deployment: [
        "System Testing",
        "Enrollment & Compensation Validation",
        "Manager & Employee Training",
        "Go-Live Support",
        "Post-Implementation Optimization",
      ],
    },

    integrations: [
      "HCM Core",
      "Payroll",
      "Performance Management",
      "Insurance Carriers",
      "Third-party Administrators",
      "Market Data Providers",
      "Stock Administration",
      "Banking",
    ],
  },
  planning: {
    // icon: Target,
    title: "Workday Adaptive Planning",
    // category: "Planning",
    description:
      "Financial planning, budgeting, and forecasting for strategic planning.",
    overview:
      "Workday Adaptive Planning provides comprehensive financial and workforce planning capabilities with real-time modeling, scenario planning, and collaborative budgeting processes.",
    features: [
      "Financial Planning",
      "Workforce Planning",
      "Budget Management",
      "Scenario Modeling",
      "Rolling Forecasts",
      "Plan Collaboration",
    ],
    technologies: [
      "Workday Adaptive Planning",
      "Workday HCM Planning",
      "Workday Analytics",
      "Workday Prism",
    ],
    benefits: [
      "Strategic Planning",
      "Agile Budgeting",
      "Scenario Analysis",
      "Collaborative Planning",
      "Real-time Insights",
      "Resource Optimization",
    ],
    process: [
      "Planning Strategy",
      "Model Design",
      "Budget Setup",
      "Forecasting",
      "Collaboration Setup",
      "Training",
    ],
    implementation: {
      discovery: [
        "Planning Requirements",
        "Model Requirements",
        "Budget Process",
        "Forecasting Needs",
        "Collaboration Requirements",
      ],
      design: [
        "Model Configuration",
        "Budget Setup",
        "Forecasting Design",
        "Collaboration Setup",
        "Reporting Design",
      ],
      deployment: [
        "Model Testing",
        "Process Testing",
        "User Training",
        "Go-Live Support",
        "Ongoing Planning Support",
      ],
    },
    integrations: [
      "HCM Core",
      "Financial Systems",
      "ERP Systems",
      "Analytics",
      "External Data Sources",
      "BI Tools",
    ],
  },
  expenses: {
    // icon: FileText,
    title: "Workday Expenses",
    // category: "Expenses",
    description:
      "Expense reporting, travel management, and expense analytics for expense management.",
    overview:
      "Workday Expenses streamlines expense reporting with mobile receipt capture, automated approval workflows, and integration with travel and procurement systems.",
    features: [
      "Expense Reporting",
      "Mobile Receipt Capture",
      "Travel Integration",
      "Automated Approvals",
      "Expense Analytics",
      "Compliance Management",
    ],
    technologies: [
      "Workday Expenses",
      "Workday Mobile",
      "Workday Travel",
      "Workday Analytics",
    ],
    benefits: [
      "Mobile Convenience",
      "Automated Processing",
      "Policy Compliance",
      "Real-time Visibility",
      "Cost Control",
      "Employee Satisfaction",
    ],
    process: [
      "Expense Policy Setup",
      "Approval Workflows",
      "Mobile Configuration",
      "Analytics Setup",
      "Integration",
      "Training",
    ],
    implementation: {
      discovery: [
        "Expense Policies",
        "Approval Process",
        "Travel Requirements",
        "Integration Needs",
        "Mobile Requirements",
      ],
      design: [
        "Policy Configuration",
        "Workflow Setup",
        "Mobile Setup",
        "Integration Design",
        "Reporting Setup",
      ],
      deployment: [
        "Policy Testing",
        "Workflow Testing",
        "Mobile Testing",
        "User Training",
        "Go-Live Support",
      ],
    },
    integrations: [
      "HCM Core",
      "Payroll",
      "Travel Systems",
      "Credit Card Systems",
      "ERP Systems",
      "Banking",
    ],
  },
  procurement: {
    // icon: Target,
    title: "Workday Procurement",
    // category: "Procurement",
    description:
      "Purchase requisitions, supplier management, and procurement analytics for procurement management.",
    overview:
      "Workday Procurement provides comprehensive procurement capabilities with requisition management, supplier onboarding, and spend analytics for efficient procurement operations.",
    features: [
      "Purchase Requisitions",
      "Supplier Management",
      "Catalog Management",
      "Contract Management",
      "Procurement Analytics",
      "Approval Workflows",
    ],
    technologies: [
      "Workday Procurement",
      "Workday Supplier Network",
      "Workday Analytics",
      "Workday Mobile",
    ],
    benefits: [
      "Streamlined Procurement",
      "Supplier Visibility",
      "Cost Control",
      "Compliance Management",
      "Process Efficiency",
      "Strategic Sourcing",
    ],
    process: [
      "Procurement Strategy",
      "Supplier Onboarding",
      "Catalog Setup",
      "Workflow Configuration",
      "Analytics Setup",
      "Training",
    ],
    implementation: {
      discovery: [
        "Procurement Process",
        "Supplier Requirements",
        "Catalog Needs",
        "Approval Process",
        "Analytics Requirements",
      ],
      design: [
        "Process Configuration",
        "Supplier Setup",
        "Catalog Management",
        "Workflow Design",
        "Reporting Setup",
      ],
      deployment: [
        "Process Testing",
        "Supplier Testing",
        "User Training",
        "Go-Live Support",
        "Ongoing Procurement Support",
      ],
    },
    integrations: [
      "HCM Core",
      "Financial Systems",
      "Supplier Systems",
      "ERP Systems",
      "Banking",
      "Third-party Catalogs",
    ],
  },
  inventory: {
    // icon: Database,
    title: "Workday Inventory",
    // category: "Operations",
    description:
      "Inventory management, stock tracking, and supply chain for operational efficiency.",
    overview:
      "Workday Inventory provides real-time inventory tracking, automated replenishment, and supply chain visibility for efficient inventory management operations.",
    features: [
      "Inventory Tracking",
      "Stock Management",
      "Automated Replenishment",
      "Supply Chain Visibility",
      "Inventory Analytics",
      "Warehouse Management",
    ],
    technologies: [
      "Workday Inventory",
      "Workday Supply Chain",
      "Workday Analytics",
      "Workday Mobile",
    ],
    benefits: [
      "Real-time Visibility",
      "Automated Replenishment",
      "Cost Optimization",
      "Supply Chain Efficiency",
      "Reduced Stockouts",
      "Analytics Insights",
    ],
    process: [
      "Inventory Strategy",
      "System Configuration",
      "Replenishment Setup",
      "Analytics Configuration",
      "Integration",
      "Training",
    ],
    implementation: {
      discovery: [
        "Inventory Requirements",
        "Supply Chain Analysis",
        "Replenishment Strategy",
        "Integration Needs",
        "Analytics Requirements",
      ],
      design: [
        "System Configuration",
        "Replenishment Setup",
        "Supply Chain Setup",
        "Analytics Design",
        "Mobile Configuration",
      ],
      deployment: [
        "System Testing",
        "Process Testing",
        "User Training",
        "Go-Live Support",
        "Ongoing Inventory Support",
      ],
    },
    integrations: [
      "Procurement",
      "Financial Systems",
      "Supplier Systems",
      "Warehouse Systems",
      "ERP Systems",
      "Third-party Logistics",
    ],
  },
  projects: {
    // icon: Layers,
    title: "Workday Projects",
    // category: "Projects",
    description:
      "Project management, resource allocation, and project analytics for project delivery.",
    overview:
      "Workday Projects provides comprehensive project management with resource planning, time tracking, and financial management for successful project delivery.",
    features: [
      "Project Management",
      "Resource Allocation",
      "Time Tracking",
      "Budget Management",
      "Project Analytics",
      "Collaboration Tools",
    ],
    technologies: [
      "Workday Projects",
      "Workday Resource Management",
      "Workday Time Tracking",
      "Workday Analytics",
    ],
    benefits: [
      "Project Visibility",
      "Resource Optimization",
      "Budget Control",
      "Delivery Success",
      "Team Collaboration",
      "Performance Insights",
    ],
    process: [
      "Project Strategy",
      "Resource Planning",
      "Project Setup",
      "Time Tracking",
      "Analytics Configuration",
      "Training",
    ],
    implementation: {
      discovery: [
        "Project Requirements",
        "Resource Strategy",
        "Time Tracking Needs",
        "Budget Management",
        "Analytics Requirements",
      ],
      design: [
        "Project Configuration",
        "Resource Setup",
        "Time Tracking Setup",
        "Budget Management",
        "Reporting Design",
      ],
      deployment: [
        "Project Testing",
        "Resource Testing",
        "User Training",
        "Go-Live Support",
        "Ongoing Project Support",
      ],
    },
    integrations: [
      "HCM Core",
      "Time Tracking",
      "Financial Systems",
      "Project Tools",
      "Collaboration Tools",
      "External Systems",
    ],
  },
  analytics: {
    // icon: BarChart3,
    title: "Workday Analytics",
    // category: "Analytics",
    description:
      "Workforce analytics, reporting, and business intelligence for data-driven decisions.",
    overview:
      "Workday Analytics provides powerful workforce analytics with real-time dashboards, predictive insights, and self-service reporting for data-driven HR decisions.",
    features: [
      "Workforce Analytics",
      "Real-time Dashboards",
      "Predictive Analytics",
      "Self-Service Reporting",
      "Benchmarking",
      "Advanced Analytics",
    ],
    technologies: [
      "Workday Analytics",
      "Workday Prism",
      "Workday Discovery Board",
      "Workday Benchmarking",
    ],
    benefits: [
      "Data-driven Decisions",
      "Predictive Insights",
      "Self-Service Analytics",
      "Benchmarking",
      "Real-time Visibility",
      "Strategic Planning",
    ],
    process: [
      "Analytics Strategy",
      "Dashboard Design",
      "Report Development",
      "Predictive Modeling",
      "User Training",
      "Optimization",
    ],
    implementation: {
      discovery: [
        "Analytics Requirements",
        "Dashboard Needs",
        "Reporting Requirements",
        "Predictive Needs",
        "User Requirements",
      ],
      design: [
        "Analytics Configuration",
        "Dashboard Design",
        "Report Development",
        "Predictive Setup",
        "Security Configuration",
      ],
      deployment: [
        "Analytics Testing",
        "Dashboard Testing",
        "User Training",
        "Go-Live Support",
        "Ongoing Analytics Support",
      ],
    },
    integrations: [
      "HCM Core",
      "All Workday Modules",
      "External Data Sources",
      "BI Tools",
      "Benchmarking Data",
      "Third-party Analytics",
    ],
  },
  reporting: {
    // icon: FileText,
    title: "Workday Reporting",
    // category: "Reporting",
    description:
      "Custom reports, scheduled reporting, and report sharing for comprehensive reporting.",
    overview:
      "Workday Reporting provides flexible reporting capabilities with drag-and-drop report building, scheduled delivery, and comprehensive sharing options for all reporting needs.",
    features: [
      "Custom Reports",
      "Scheduled Reporting",
      "Report Sharing",
      "Matrix Reports",
      "Composite Reports",
      "Report Security",
    ],
    technologies: [
      "Workday Reporting",
      "Workday Writer",
      "Workday Analytics",
      "Workday Mobile",
    ],
    benefits: [
      "Flexible Reporting",
      "Automated Delivery",
      "Self-Service Reports",
      "Real-time Data",
      "Mobile Access",
      "Security Controls",
    ],
    process: [
      "Reporting Strategy",
      "Report Design",
      "Security Setup",
      "Scheduling Configuration",
      "Training",
      "Optimization",
    ],
    implementation: {
      discovery: [
        "Reporting Requirements",
        "Security Needs",
        "Scheduling Requirements",
        "Sharing Needs",
        "Performance Requirements",
      ],
      design: [
        "Report Configuration",
        "Security Setup",
        "Scheduling Setup",
        "Sharing Configuration",
        "Performance Optimization",
      ],
      deployment: [
        "Report Testing",
        "Security Testing",
        "User Training",
        "Go-Live Support",
        "Ongoing Report Support",
      ],
    },
    integrations: [
      "All Workday Modules",
      "External Systems",
      "BI Tools",
      "Email Systems",
      "File Systems",
      "Mobile Apps",
    ],
  },
  security: {
    // icon: Shield,
    title: "Workday Security",
    // category: "Security",
    description:
      "User access, role-based security, and audit compliance for system security.",
    overview:
      "Workday Security provides comprehensive security management with role-based access, audit trails, and compliance reporting to ensure data protection and regulatory compliance.",
    features: [
      "Role-based Security",
      "User Access Management",
      "Audit Trails",
      "Compliance Reporting",
      "Single Sign-On",
      "Multi-factor Authentication",
    ],
    technologies: [
      "Workday Security",
      "Workday SSO",
      "Workday MFA",
      "Workday Audit",
      "Workday Identity",
    ],
    benefits: [
      "Enhanced Security",
      "Compliance Management",
      "Access Control",
      "Audit Readiness",
      "Risk Mitigation",
      "User Experience",
    ],
    process: [
      "Security Strategy",
      "Role Design",
      "Access Configuration",
      "Audit Setup",
      "Compliance Configuration",
      "Training",
    ],
    implementation: {
      discovery: [
        "Security Requirements",
        "Compliance Needs",
        "Role Framework",
        "Access Requirements",
        "Audit Requirements",
      ],
      design: [
        "Security Configuration",
        "Role Setup",
        "Access Design",
        "Audit Configuration",
        "Compliance Setup",
      ],
      deployment: [
        "Security Testing",
        "Access Testing",
        "User Training",
        "Go-Live Support",
        "Ongoing Security Management",
      ],
    },
    integrations: [
      "All Workday Modules",
      "Identity Providers",
      "SSO Systems",
      "MFA Systems",
      "Audit Systems",
      "Compliance Tools",
    ],
  },
  integration: {
    // icon: Layers,
    title: "Workday Integration",
    // category: "Integration",
    description:
      "System integrations, data synchronization, and API management for seamless connectivity.",
    overview:
      "Workday Integration provides comprehensive integration capabilities with pre-built connectors, custom integrations, and real-time data synchronization for seamless system connectivity.",
    features: [
      "Pre-built Connectors",
      "Custom Integrations",
      "Real-time Synchronization",
      "Data Transformation",
      "Error Handling",
      "Integration Monitoring",
    ],
    technologies: [
      "Workday Integration Cloud",
      "Workday Studio",
      "Workday EIB",
      "Workday Web Services",
      "Workday Connect",
    ],
    benefits: [
      "Seamless Connectivity",
      "Real-time Data",
      "Reduced Manual Work",
      "Data Accuracy",
      "System Reliability",
      "Scalable Integration",
    ],
    process: [
      "Integration Strategy",
      "System Analysis",
      "Integration Design",
      "Development",
      "Testing",
      "Deployment",
    ],
    implementation: {
      discovery: [
        "Integration Requirements",
        "System Analysis",
        "Data Mapping",
        "Performance Requirements",
        "Security Requirements",
      ],
      design: [
        "Integration Architecture",
        "Data Design",
        "Error Handling",
        "Security Setup",
        "Performance Optimization",
      ],
      deployment: [
        "Integration Testing",
        "Performance Testing",
        "Go-Live Support",
        "Monitoring Setup",
        "Ongoing Integration Support",
      ],
    },
    integrations: [
      "All External Systems",
      "ERP Systems",
      "Payroll Systems",
      "Benefits Systems",
      "Time Systems",
      "Third-party Applications",
    ],
  },
  mobile: {
    // icon: MessageSquare,
    title: "Workday Mobile",
    // category: "Mobile",
    description:
      "Mobile access, native apps, and mobile workflows for on-the-go productivity.",
    overview:
      "Workday Mobile provides comprehensive mobile access with native apps, responsive design, and mobile-optimized workflows for productivity anywhere, anytime.",
    features: [
      "Native Mobile Apps",
      "Responsive Design",
      "Mobile Workflows",
      "Offline Capability",
      "Push Notifications",
      "Mobile Security",
    ],
    technologies: [
      "Workday Mobile Apps",
      "Workday Responsive Design",
      "Workday Push Notifications",
      "Workday Mobile Security",
    ],
    benefits: [
      "Anywhere Access",
      "Improved Productivity",
      "Real-time Updates",
      "User Experience",
      "Mobile Security",
      "Offline Capability",
    ],
    process: [
      "Mobile Strategy",
      "App Configuration",
      "Workflow Optimization",
      "Security Setup",
      "Testing",
      "Deployment",
    ],
    implementation: {
      discovery: [
        "Mobile Requirements",
        "User Experience Needs",
        "Security Requirements",
        "Performance Requirements",
        "Workflow Analysis",
      ],
      design: [
        "Mobile Configuration",
        "Workflow Design",
        "Security Setup",
        "Performance Optimization",
        "User Experience Design",
      ],
      deployment: [
        "Mobile Testing",
        "User Experience Testing",
        "Training",
        "App Deployment",
        "Ongoing Mobile Support",
      ],
    },
    integrations: [
      "All Workday Modules",
      "Device Management",
      "App Stores",
      "Push Notification Services",
      "Mobile Security",
      "Analytics",
    ],
  },
  extend: {
    // icon: Cog,
    title: "Workday Extend",
    // category: "Platform",
    description:
      "Custom applications, workflow extensions, and platform development for business solutions.",
    overview:
      "Workday Extend enables custom application development on the Workday platform with low-code tools, workflow extensions, and seamless integration with core Workday functionality.",
    features: [
      "Custom Applications",
      "Workflow Extensions",
      "Low-code Development",
      "Platform Integration",
      "Custom Objects",
      "Business Logic",
    ],
    technologies: [
      "Workday Extend",
      "Workday Platform",
      "Workday APIs",
      "Workday Orchestrate",
      "Workday Studio",
    ],
    benefits: [
      "Custom Solutions",
      "Rapid Development",
      "Platform Integration",
      "Business Agility",
      "Innovation",
      "Cost Efficiency",
    ],
    process: [
      "Requirements Analysis",
      "Solution Design",
      "Development",
      "Testing",
      "Deployment",
      "Maintenance",
    ],
    implementation: {
      discovery: [
        "Business Requirements",
        "Technical Requirements",
        "Integration Needs",
        "User Experience",
        "Performance Requirements",
      ],
      design: [
        "Solution Architecture",
        "Application Design",
        "Integration Design",
        "User Interface Design",
        "Security Design",
      ],
      deployment: [
        "Development",
        "Testing",
        "User Training",
        "Go-Live Support",
        "Ongoing Development Support",
      ],
    },
    integrations: [
      "All Workday Modules",
      "External APIs",
      "Third-party Systems",
      "Custom Systems",
      "Legacy Systems",
      "Cloud Services",
    ],
  },
  prism: {
    // icon: Database,
    title: "Workday Prism Analytics",
    // category: "Analytics",
    description:
      "External data integration, advanced analytics, and data blending for comprehensive insights.",
    overview:
      "Workday Prism Analytics enables advanced analytics by blending Workday data with external data sources, providing comprehensive insights for strategic decision-making.",
    features: [
      "External Data Integration",
      "Data Blending",
      "Advanced Analytics",
      "Predictive Modeling",
      "Data Visualization",
      "Self-Service Analytics",
    ],
    technologies: [
      "Workday Prism",
      "Workday Analytics",
      "Data Integration",
      "Machine Learning",
      "Statistical Analysis",
    ],
    benefits: [
      "Comprehensive Insights",
      "External Data Integration",
      "Advanced Analytics",
      "Predictive Intelligence",
      "Self-Service Capabilities",
      "Strategic Decision Making",
    ],
    process: [
      "Data Strategy",
      "Data Integration",
      "Analytics Design",
      "Model Development",
      "Visualization",
      "Training",
    ],
    implementation: {
      discovery: [
        "Data Requirements",
        "Analytics Needs",
        "Integration Requirements",
        "Modeling Needs",
        "Visualization Requirements",
      ],
      design: [
        "Data Integration Design",
        "Analytics Configuration",
        "Model Development",
        "Visualization Design",
        "Security Setup",
      ],
      deployment: [
        "Data Testing",
        "Analytics Testing",
        "User Training",
        "Go-Live Support",
        "Ongoing Analytics Support",
      ],
    },
    integrations: [
      "Workday Analytics",
      "External Data Sources",
      "BI Tools",
      "Statistical Tools",
      "Machine Learning Platforms",
      "Data Warehouses",
    ],
  },
  student: {
    // icon: BookOpen,
    title: "Workday Student",
    // category: "Education",
    description:
      "Student information system, academic records, and student lifecycle for educational institutions.",
    overview:
      "Workday Student provides comprehensive student lifecycle management from recruitment to alumni engagement with modern technology and user experience for higher education.",
    features: [
      "Student Records",
      "Academic Planning",
      "Registration",
      "Financial Aid",
      "Student Billing",
      "Alumni Management",
    ],
    technologies: [
      "Workday Student",
      "Workday Academic Planning",
      "Workday Financial Aid",
      "Workday Student Billing",
    ],
    benefits: [
      "Student Experience",
      "Academic Success",
      "Operational Efficiency",
      "Data Integration",
      "Mobile Access",
      "Modern Technology",
    ],
    process: [
      "Implementation Strategy",
      "Data Migration",
      "Configuration",
      "Integration",
      "Testing",
      "Go-Live",
    ],
    implementation: {
      discovery: [
        "Student Requirements",
        "Academic Process",
        "Financial Aid Process",
        "Integration Requirements",
        "User Experience",
      ],
      design: [
        "System Configuration",
        "Academic Setup",
        "Financial Aid Setup",
        "Integration Design",
        "User Interface Design",
      ],
      deployment: [
        "Data Migration",
        "System Testing",
        "User Training",
        "Go-Live Support",
        "Ongoing Student Support",
      ],
    },
    integrations: [
      "Workday HCM",
      "Financial Systems",
      "Learning Management",
      "Library Systems",
      "External Systems",
      "Government Systems",
    ],
  },
};

const WorkdayDetail = () => {
  const { moduleId } = useParams<{ moduleId: string }>();

  // Force re-render when moduleId changes
  useEffect(() => {
    // Scroll to top immediately and after a short delay to ensure it works
    window.scrollTo(0, 0);
    const timeoutId = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [moduleId]);

  if (!moduleId || !workdayModuleData[moduleId]) {
    return <Navigate to="/404" replace />;
  }

  const module = workdayModuleData[moduleId];
  // const IconComponent = module.icon;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={workdayHrBg}
            alt="Workday Human Resources Management"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-hero opacity-90"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          {/* <Button
            variant="outline"
            className="mb-8 border-white text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Workday Modules
          </Button> */}

          <div className="max-w-4xl text-white">
            {/* <div className="flex items-center gap-4 mb-6">
              <div className="bg-gradient-primary p-4 rounded-xl shadow-glow">
                <IconComponent className="h-8 w-8 text-white" />
              </div>
              <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
                {module.category}
              </Badge>
            </div> */}

            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white mt-12 drop-shadow-lg">
              {module.title}
            </h1>

            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              {module.description}
            </p>
          </div>
        </div>
      </section>
      {/* Overview Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-foreground">
                Module Overview
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                {module.overview}
              </p>

              <h3 className="text-xl font-semibold mb-4 text-foreground">
                Key Benefits
              </h3>
              <div className="grid gap-3">
                {module.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className={`h-5 w-5 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} flex-shrink-0`} />
                    <span className="text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <Card className="bg-gradient-card border-0 shadow-elegant">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-primary" />
                  Core Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3">
                  {module.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-background/50 rounded-lg"
                    >
                      <div className={`w-2 h-2 ${index % 2 === 0 ? 'bg-primary shadow-glow' : 'bg-secondary shadow-glow-secondary'} rounded-full`}></div>
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 bg-accent/5">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
            Technologies & Components
          </h2>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {module.technologies.map((tech, index) => (
              <Badge
                key={index}
                variant="outline"
                className={`px-4 py-2 text-sm ${index % 2 === 0 ? 'bg-primary/5 text-primary border-primary/20' : 'bg-secondary/5 text-secondary border-secondary/20'}`}
              >
                {tech}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
            Implementation Approach
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="bg-gradient-card border-0 shadow-elegant">
              <CardHeader>
                <CardTitle className="text-center text-primary font-bold">
                  Discovery Phase
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {module.implementation.discovery.map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Check className={`h-4 w-4 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} flex-shrink-0`} />
                      <span className="text-sm text-muted-foreground">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card border-0 shadow-elegant">
              <CardHeader>
                <CardTitle className="text-center text-secondary font-bold">
                  Design Phase
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {module.implementation.design.map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Check className={`h-4 w-4 ${index % 2 === 0 ? 'text-secondary shadow-glow-secondary' : 'text-primary shadow-glow'} flex-shrink-0`} />
                      <span className="text-sm text-muted-foreground">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-card border-0 shadow-elegant">
              <CardHeader>
                <CardTitle className="text-center text-primary">
                  Deployment Phase
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {module.implementation.deployment.map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Check className={`h-4 w-4 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} flex-shrink-0`} />
                      <span className="text-sm text-muted-foreground">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Integration Points */}
      <section className="py-16 bg-accent/5">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
            Integration Capabilities
          </h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-4xl mx-auto">
            {module.integrations.map((integration, index) => (
              <div
                key={index}
                className="bg-background/50 p-4 rounded-lg text-center border border-border/50"
              >
                <span className="text-sm font-medium text-foreground">
                  {integration}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WorkdayDetail;
